(function () {
  'use strict';

  const PRODUCT_PREVIEW_SELECTOR =
    '.option-preview img, .size-guide-lightbox-trigger';

  function readConfig() {
    const node = document.getElementById('LSProductMediaConfig');

    if (!node) {
      return {
        closeLabel: 'Close',
        previewAlt: '',
      };
    }

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error('[Love Smiths] Invalid product media configuration:', error);

      return {
        closeLabel: 'Close',
        previewAlt: '',
      };
    }
  }

  function loadDeferredFrame(frame) {
    if (!frame || frame.hasAttribute('src') || !frame.dataset.src) return;

    frame.src = frame.dataset.src;
    frame.removeAttribute('data-src');
  }

  function loadPanelMedia(panel) {
    if (!panel) return;

    panel.querySelectorAll('iframe[data-src]').forEach(loadDeferredFrame);
  }

  function loadInitiallyOpenPanels() {
    document
      .querySelectorAll('.variant-more-info-content.open')
      .forEach(loadPanelMedia);
  }

  function makePreviewAccessible(image) {
    if (!image || image.dataset.lsPreviewAccessible === '1') return;

    image.tabIndex = 0;
    image.setAttribute('role', 'button');
    image.dataset.lsPreviewAccessible = '1';
  }

  function initialisePreviewAccessibility() {
    document.querySelectorAll(PRODUCT_PREVIEW_SELECTOR).forEach(makePreviewAccessible);

    const productRoot = document.querySelector(
      '[id^="MainProduct-"], .product-page'
    );

    if (!productRoot) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(PRODUCT_PREVIEW_SELECTOR)) {
            makePreviewAccessible(node);
          }

          node.querySelectorAll?.(PRODUCT_PREVIEW_SELECTOR).forEach(
            makePreviewAccessible
          );
        });
      });
    });

    observer.observe(productRoot, { childList: true, subtree: true });
  }

  function initialiseDesktopLightbox(config) {
    if (window.matchMedia('(max-width: 1024px)').matches) return;

    let lightbox = document.querySelector('.variant-lightbox');
    let lightboxImg = lightbox
      ? lightbox.querySelector('.variant-lightbox-img')
      : null;
    let closeButton = lightbox
      ? lightbox.querySelector('.variant-lightbox-close')
      : null;
    let previouslyFocused = null;

    function ensureLightbox() {
      if (lightbox && lightboxImg && closeButton) return;

      lightbox = document.createElement('div');
      lightbox.className = 'variant-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-hidden', 'true');
      lightbox.innerHTML = `
        <div class="variant-lightbox-content" role="document">
          <button
            type="button"
            class="variant-lightbox-close"
            aria-label="${config.closeLabel || 'Close'}"
          >
            ✕
          </button>
          <img
            src=""
            alt="${config.previewAlt || ''}"
            class="variant-lightbox-img"
            loading="lazy"
            decoding="async"
          >
        </div>
      `;

      document.body.appendChild(lightbox);
      lightboxImg = lightbox.querySelector('.variant-lightbox-img');
      closeButton = lightbox.querySelector('.variant-lightbox-close');
    }

    function closeLightbox() {
      if (!lightbox || lightbox.getAttribute('aria-hidden') === 'true') return;

      lightbox.classList.remove('open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    }

    function openLightbox(image) {
      ensureLightbox();

      const source =
        image.currentSrc ||
        image.getAttribute('data-large') ||
        image.getAttribute('src');

      if (!source) return;

      previouslyFocused = document.activeElement;
      lightboxImg.src = source;
      lightboxImg.alt = image.getAttribute('alt') || config.previewAlt || '';
      lightbox.classList.add('open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      closeButton.focus();
    }

    document.addEventListener(
      'pointerover',
      function (event) {
        const image = event.target.closest(PRODUCT_PREVIEW_SELECTOR);

        if (image) image.style.cursor = 'zoom-in';
      },
      { passive: true }
    );

    document.addEventListener('click', function (event) {
      const image = event.target.closest(PRODUCT_PREVIEW_SELECTOR);

      if (image) {
        event.preventDefault();
        openLightbox(image);
        return;
      }

      if (!lightbox) return;

      if (
        event.target === lightbox ||
        event.target.closest('.variant-lightbox-close')
      ) {
        closeLightbox();
      }
    });

    document.addEventListener('keydown', function (event) {
      const image = event.target.closest?.(PRODUCT_PREVIEW_SELECTOR);

      if (image && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        openLightbox(image);
        return;
      }

      if (!lightbox || lightbox.getAttribute('aria-hidden') === 'true') return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        closeButton.focus();
      }
    });
  }

  function bindGalleryAltUpdates(config) {
    document.addEventListener('click', function (event) {
      const thumbnail = event.target.closest('.gallery-thumbnails .thumbnail');

      if (!thumbnail) return;

      const productRoot = thumbnail.closest('[id^="MainProduct-"], .product-page');
      const mainImage = productRoot
        ? productRoot.querySelector('.main-banner')
        : document.querySelector('.main-banner');

      if (!mainImage) return;

      const selectedAlt = thumbnail.getAttribute('alt');
      mainImage.alt = selectedAlt || config.previewAlt || '';
    });
  }

  function syncMoreInfoAccessibility() {
    document.querySelectorAll('.variant-more-info-btn').forEach((button, index) => {
      const key = button.dataset.info;
      if (!key) return;

      const scope = button.closest('[id^="MainProduct-"], .product-page') || document;
      const panel = scope.querySelector(
        `.variant-more-info-content[data-info-target="${CSS.escape(key)}"]`
      );

      if (!panel) return;

      if (!panel.id) panel.id = `ls-more-info-${key}-${index}`;
      button.setAttribute('aria-controls', panel.id);

      const expanded = panel.classList.contains('open');
      button.setAttribute('aria-expanded', String(expanded));
      panel.setAttribute('aria-hidden', String(!expanded));
    });
  }

  function bindDeferredMoreInfoMedia() {
    document.addEventListener('click', function (event) {
      const button = event.target.closest('.variant-more-info-btn');

      if (!button) return;

      const key = button.dataset.info;

      if (!key) return;

      window.setTimeout(function () {
        const scope = button.closest('[id^="MainProduct-"], .product-page') || document;
        const panel = scope.querySelector(
          `.variant-more-info-content[data-info-target="${CSS.escape(key)}"]`
        );

        loadPanelMedia(panel);
        syncMoreInfoAccessibility();
      }, 0);
    });

    syncMoreInfoAccessibility();
    loadInitiallyOpenPanels();
  }



  function bindMoreInfoScrollStabiliser() {
    document.addEventListener('click', function (event) {
      const button = event.target.closest('.variant-more-info-btn');
      if (!button) return;

      const key = button.dataset.info;
      if (!key) return;

      const scope = button.closest('[id^="MainProduct-"], .product-page') || document;
      const target = scope.querySelector(
        `.variant-more-info-content[data-info-target="${CSS.escape(key)}"]`
      );
      if (!target) return;

      const targetWasOpen = target.classList.contains('open');
      const anotherPanelWasOpen = Array.from(
        scope.querySelectorAll('.variant-more-info-content.open')
      ).some((panel) => panel !== target);

      if (targetWasOpen || !anotherPanelWasOpen) return;

      window.setTimeout(function () {
        if (!target.classList.contains('open')) return;

        const stickyHeader = document.querySelector(
          '.shopify-section-header-sticky, sticky-header, .header-wrapper'
        );
        const headerHeight = stickyHeader
          ? Math.ceil(stickyHeader.getBoundingClientRect().height)
          : 0;
        const safeOffset = headerHeight + 16;
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({
          top: Math.max(targetTop - safeOffset, 0),
          behavior: 'auto'
        });
      }, 0);
    }, true);
  }

  function bindShippingAccessibility() {
    document.addEventListener('click', function (event) {
      const toggle = event.target.closest('.shipping-toggle');
      if (!toggle) return;

      window.setTimeout(function () {
        const contentId = toggle.getAttribute('aria-controls');
        const content = contentId ? document.getElementById(contentId) : null;
        if (!content) return;

        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        content.setAttribute('aria-hidden', String(!expanded));
      }, 0);
    });

    document.querySelectorAll('.shipping-toggle[aria-controls]').forEach((toggle) => {
      const content = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!content) return;

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      content.setAttribute('aria-hidden', String(!expanded));
    });
  }

  function initialise() {
    const config = readConfig();

    initialisePreviewAccessibility();
    initialiseDesktopLightbox(config);
    bindGalleryAltUpdates(config);
    bindMoreInfoScrollStabiliser();
    bindDeferredMoreInfoMedia();
    bindShippingAccessibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
